// InviteMemberModal.jsx
import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';

const InviteMemberModal = ({ 
  isOpen, 
  onClose, 
  onInvite, 
  availableRoles, 
  currentTeamId,
  showNotification // Updated prop name
}) => {
  const [formData, setFormData] = useState({
    email: '',
    roleId: '',
    teamId: currentTeamId,
    email_notification: true // Add this with default true
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, teamId: currentTeamId }));
  }, [currentTeamId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.custom-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.email?.trim()) {
      if (showNotification) {
        showNotification('Please enter an email address', 'warning', 4000);
      }
      return;
    }

    if (!validateEmail(formData.email)) {
      if (showNotification) {
        showNotification('Please enter a valid email address', 'warning', 4000);
      }
      return;
    }

    if (!formData.roleId) {
      if (showNotification) {
        showNotification('Please select a role for the team member', 'warning', 4000);
      }
      return;
    }

    try {
      setIsSubmitting(true);
      await onInvite(formData);
      // Parent component handles success toast
    } catch (error) {
      // Parent component handles error toast
      console.error('Error sending invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing during submission
    
    setFormData({ email: '', roleId: '', teamId: currentTeamId, email_notification: true }); // Reset with default true
    setIsDropdownOpen(false);
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleSelect = (roleId) => {
    handleInputChange('roleId', roleId);
    setIsDropdownOpen(false);
  };

  // Handle Enter key submission
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
  };

  const selectedRole = availableRoles.find(r => r.sgl_id == formData.roleId);

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modern-team-modal"
      overlayClassName="modern-team-overlay"
      ariaHideApp={false}
    >
      <div className="modern-modal-header">
        <h2>Invite Team Member</h2>
        <button 
          className="modern-close-btn" 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      <div className="modern-modal-body">
        <div className="modern-form-group">
          <label>Email Address *</label>
          <input
            type="email"
            placeholder="Enter email address"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onKeyPress={handleKeyPress}
            className="modern-input"
            disabled={isSubmitting}
            autoFocus
          />
          {formData.email && !validateEmail(formData.email) && (
            <div style={{ 
              fontSize: '12px', 
              color: '#dc2626',
              marginTop: '4px'
            }}>
              Please enter a valid email address
            </div>
          )}
        </div>

        <div className="modern-form-group">
          <label>Role *</label>
          <div className="custom-dropdown">
            <div 
              className={`custom-dropdown-trigger ${isDropdownOpen ? 'open' : ''} ${isSubmitting ? 'disabled' : ''}`}
              onClick={() => !isSubmitting && setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="dropdown-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="5" stroke="currentColor" strokeWidth="2"/>
                  <path d="M20 21a8 8 0 0 0-16 0" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="dropdown-text">
                {selectedRole ? selectedRole.name : 'Select a role...'}
              </span>
              <div className="dropdown-arrow">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </div>
            
            {isDropdownOpen && !isSubmitting && (
              <div className="custom-dropdown-menu">
                {availableRoles.map((role) => (
                  <div
                    key={role.sgl_id}
                    className={`dropdown-option ${selectedRole?.sgl_id === role.sgl_id ? 'selected' : ''}`}
                    onClick={() => handleRoleSelect(role.sgl_id)}
                  >
                    <div className="option-name">{role.name}</div>
                    <div className="option-description">{role.description}</div>
                    {selectedRole?.sgl_id === role.sgl_id && (
                      <div className="option-check">✓</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedRole && (
            <div className="modern-role-desc">
              {selectedRole.description}
            </div>
          )}
        </div>

        {/* Add Email Notification Checkbox */}
        <div className="modern-form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.email_notification}
              onChange={(e) => handleInputChange('email_notification', e.target.checked)}
              disabled={isSubmitting}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer',
                accentColor: '#0ea5e9'
              }}
            />
            <span style={{ userSelect: 'none' }}>Receive email notifications</span>
          </label>
        </div>
      </div>

      <div className="modern-modal-footer">
        <button 
          className="modern-btn-cancel" 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          className="modern-btn-primary" 
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.email?.trim() || !formData.roleId}
        >
          {isSubmitting ? (
            <>
              <span style={{ 
                display: 'inline-block', 
                width: '14px', 
                height: '14px', 
                border: '2px solid transparent', 
                borderTop: '2px solid currentColor', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                marginRight: '8px'
              }}></span>
              Sending...
            </>
          ) : (
            'Send Invitation'
          )}
        </button>
      </div>

      {/* Add CSS for spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .custom-dropdown-trigger.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </ReactModal>
  );
};

// EditMemberModal.jsx
const EditMemberModal = ({ 
  isOpen, 
  onClose, 
  member, 
  onUpdate, 
  availableRoles,
  showNotification // Updated prop name
}) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (member) {
      setSelectedMember({ 
        ...member, 
        roleId: member.role?.sgl_id || ''
      });
    }
  }, [member]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.custom-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSubmit = async () => {
    if (!selectedMember) {
      if (showNotification) {
        showNotification('No member selected for update', 'error', 4000);
      }
      return;
    }

    if (!selectedMember.roleId) {
      if (showNotification) {
        showNotification('Please select a role for the team member', 'warning', 4000);
      }
      return;
    }

    // Check if anything actually changed
    const originalRole = member?.role?.sgl_id;
    const originalEmailNotification = member?.email_notification !== undefined ? member.email_notification : true;
    if (selectedMember.roleId == originalRole && selectedMember.email_notification === originalEmailNotification) {
      if (showNotification) {
        showNotification('No changes detected', 'info', 3000);
      }
      handleClose();
      return;
    }

    try {
      setIsSubmitting(true);
      await onUpdate(selectedMember);
      // Parent component handles success toast
    } catch (error) {
      // Parent component handles error toast
      console.error('Error updating member:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing during submission
    
    setSelectedMember(null);
    setIsDropdownOpen(false);
    onClose();
  };

  const handleRoleChange = (roleId) => {
    setSelectedMember(prev => ({ ...prev, roleId }));
    setIsDropdownOpen(false);
  };

  const selectedRole = availableRoles.find(r => r.sgl_id == selectedMember?.roleId);
  const originalEmailNotification = member?.email_notification !== undefined ? member.email_notification : true;
  const canManageMembers = member?.canManageMembers !== false; // Default to true if not specified

  const hasChanges = selectedMember && (
    member?.role?.sgl_id != selectedMember.roleId || 
    originalEmailNotification !== selectedMember.email_notification
  );

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modern-team-modal"
      overlayClassName="modern-team-overlay"
      ariaHideApp={false}
    >
      <div className="modern-modal-header">
        <h2>Edit Team Member</h2>
        <button 
          className="modern-close-btn" 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      <div className="modern-modal-body">
        {selectedMember && (
          <>
            <div className="modern-form-group">
              <label>Name</label>
              <input
                type="text"
                value={selectedMember.name || 'Not provided'}
                disabled
                className="modern-input modern-input-disabled"
              />
            </div>

            <div className="modern-form-group">
              <label>Email</label>
              <input
                type="email"
                value={selectedMember.email || 'Not provided'}
                disabled
                className="modern-input modern-input-disabled"
              />
            </div>

            <div className="modern-form-group">
              <label>Role {canManageMembers && '*'}</label>
              <div className="custom-dropdown">
                <div 
                  className={`custom-dropdown-trigger ${isDropdownOpen ? 'open' : ''} ${isSubmitting || !canManageMembers ? 'disabled' : ''}`}
                  onClick={() => !isSubmitting && canManageMembers && setIsDropdownOpen(!isDropdownOpen)}
                  style={!canManageMembers ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                >
                  <div className="dropdown-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="5" stroke="currentColor" strokeWidth="2"/>
                      <path d="M20 21a8 8 0 0 0-16 0" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <span className="dropdown-text">
                    {selectedRole ? selectedRole.name : 'Select a role...'}
                  </span>
                  <div className="dropdown-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                </div>
                
                {isDropdownOpen && !isSubmitting && canManageMembers && (
                  <div className="custom-dropdown-menu">
                    {availableRoles.map((role) => (
                      <div
                        key={role.sgl_id}
                        className={`dropdown-option ${selectedRole?.sgl_id === role.sgl_id ? 'selected' : ''}`}
                        onClick={() => handleRoleChange(role.sgl_id)}
                      >
                        <div className="option-name">{role.name}</div>
                        <div className="option-description">{role.description}</div>
                        {selectedRole?.sgl_id === role.sgl_id && (
                          <div className="option-check">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedRole && (
                <div className="modern-role-desc">
                  {selectedRole.description}
                </div>
              )}
              {canManageMembers && hasChanges && member?.role?.sgl_id != selectedMember.roleId && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#0369a1',
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '4px',
                  border: '1px solid #e0f2fe'
                }}>
                  Role will be changed from "{member?.role?.name}" to "{selectedRole?.name}"
                </div>
              )}
            </div>

            {/* Email Notification Checkbox */}
            <div className="modern-form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedMember.email_notification}
                  onChange={(e) => setSelectedMember(prev => ({ ...prev, email_notification: e.target.checked }))}
                  disabled={isSubmitting}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#0ea5e9'
                  }}
                />
                <span style={{ userSelect: 'none' }}>Receive email notifications</span>
              </label>
            </div>
          </>
        )}
      </div>

      <div className="modern-modal-footer">
        <button 
          className="modern-btn-cancel" 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          className="modern-btn-primary" 
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedMember?.roleId || !hasChanges}
        >
          {isSubmitting ? (
            <>
              <span style={{ 
                display: 'inline-block', 
                width: '14px', 
                height: '14px', 
                border: '2px solid transparent', 
                borderTop: '2px solid currentColor', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                marginRight: '8px'
              }}></span>
              Updating...
            </>
          ) : (
            'Update Member'
          )}
        </button>
      </div>

      {/* Add CSS for spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .custom-dropdown-trigger.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </ReactModal>
  );
};

export { InviteMemberModal, EditMemberModal };